import React, { useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { PapiDeviation } from '@wix/da-papi-types';
import { MobileContext } from '@wix/da-react-context/pkg/MobileContext';
import SophisticatedTooltipLink from '@wix/da-ds/pkg/tooltips/SophisticatedTooltipLink';
import { useSubscriptionTier } from '../../../utils/hooks/useSubscriptionTier';
import { useSubscriptionLockFTUE } from '../../../Tier/ftue/useSubscriptionLockFTUE';
import SubscriptionLockLitFTUETooltip from '../../../Tier/ftue/SubscriptionLockLitFTUE/SubscriptionLockLitFTUETooltip';
import { SubscriptionPurchaseFTUEContext } from '../../../Tier/ftue/SubscriptionPurchaseFTUE/SubscriptionPurchaseFTUEContext';
import { getDeviationType } from '../../../DeviationViews/_helpers';
import { DeviationTypes } from '../../../types/deviation';
import SophisticatedTooltip from '@wix/da-ds/pkg/tooltips/dsPreset/SophisticatedTooltip';
import SubscribeToUnlockTooltip from '../SubscribeToUnlockTooltip';

const TOOLTIP_WIDTH_PX = 256;

export interface Props {
  deviation: PapiDeviation;
  linkClassName?: string;
}

/** The SubscribersOnly element is wrapped by a pretty wide range of tooltips
 *  based on the viewer's relation to the deviation. */
const SubscribersOnlyTooltip: React.FC<React.PropsWithChildren<Props>> = ({
  deviation,
  linkClassName,
  children,
}) => {
  const { t } = useTranslation();
  const { tier, isTier, isUserSubscribed, subscriptionsTabUrl, needsSubscription } =
    useSubscriptionTier(deviation);
  const { isSubscriptionLockFTUEShown } = useSubscriptionLockFTUE(deviation);
  const isMobile = useContext(MobileContext);
  const deviationType = getDeviationType(deviation);
  const isLitOrJournal = [DeviationTypes.JOURNAL, DeviationTypes.LITERATURE].includes(
    deviationType
  );

  const subscriptionPurchaseFTUEPhase = useContext(SubscriptionPurchaseFTUEContext);

  if (!tier) {
    return <>{children}</>;
  }

  // unsubscribed tier or in-tier
  const isAnUnsubscribedTier = isTier && !isUserSubscribed;

  if (needsSubscription || isAnUnsubscribedTier) {
    // if they've never seen a subscription lock before show a special tooltip
    const showLockFTUETooltip = isSubscriptionLockFTUEShown && isMobile && isLitOrJournal;
    return showLockFTUETooltip ? (
      <SubscriptionLockLitFTUETooltip deviation={deviation} linkClassName={linkClassName}>
        {children}
      </SubscriptionLockLitFTUETooltip>
    ) : (
      <SubscribeToUnlockTooltip deviation={deviation}>{children}</SubscribeToUnlockTooltip>
    );
  }

  // subscribed
  const subscribedHeading =
    deviationType === DeviationTypes.JOURNAL
      ? t('deviation.tier.subscribersOnly.tooltip.unlockedJournal')
      : deviationType === DeviationTypes.STATUS
        ? t('deviation.tier.subscribersOnly.tooltip.unlockedPost')
        : t('deviation.tier.subscribersOnly.tooltip.unlockedContent');
  return (
    <SophisticatedTooltip
      isVisible={subscriptionPurchaseFTUEPhase === 'tooltips'}
      size="large"
      placement="top"
      width={TOOLTIP_WIDTH_PX}
      heading={subscribedHeading}
      description={tier.title}
      postfix={
        <SophisticatedTooltipLink href={subscriptionsTabUrl} className={linkClassName}>
          {t('deviation.tier.subscribersOnly.tooltip.subscriptionsLink')}
        </SophisticatedTooltipLink>
      }
    >
      {children}
    </SophisticatedTooltip>
  );
};
SubscribersOnlyTooltip.displayName = 'SubscribersOnlyTooltip';
export default SubscribersOnlyTooltip;
